import { useEffect, useRef } from 'react';
import { Phone, PhoneOff, User, Video } from 'lucide-react';
import type { ConversationCall } from '../../types';

interface IncomingCallModalProps {
  isOpen: boolean;
  call: ConversationCall | null;
  onAccept: () => void;
  onReject: () => void;
  accepting?: boolean;
  rejecting?: boolean;
}

export default function IncomingCallModal({
  isOpen,
  call,
  onAccept,
  onReject,
  accepting = false,
  rejecting = false,
}: IncomingCallModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const acceptRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    acceptRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen || !call) return null;

  const caller = call.caller;
  const title = caller ? `${caller.name} ${caller.surname}` : 'Bilinmeyen Arama';
  const isVideo = call.call_type === 'video';
  const subtitle = isVideo ? 'Gelen görüntülü arama' : 'Gelen sesli arama';
  const initials = caller
    ? `${caller.name?.[0] ?? ''}${caller.surname?.[0] ?? ''}`
    : '';

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="incoming-call-title"
      aria-live="assertive"
      data-testid="incoming-call-modal"
    >
      <div className="flex w-full max-w-md flex-col items-center rounded-t-3xl bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-2xl sm:rounded-2xl sm:pb-6">
        <div
          className="mb-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700"
          data-testid="incoming-call-type-label"
        >
          {subtitle}
        </div>

        {caller?.avatar ? (
          <img
            src={caller.avatar}
            alt=""
            className="mb-3 mt-4 h-20 w-20 rounded-full object-cover ring-4 ring-blue-100 sm:h-24 sm:w-24"
          />
        ) : (
          <div className="mb-3 mt-4 flex h-20 w-20 items-center justify-center rounded-full bg-blue-100 text-2xl font-semibold text-blue-700 ring-4 ring-blue-50 sm:h-24 sm:w-24">
            {initials || <User className="h-9 w-9 sm:h-10 sm:w-10" />}
          </div>
        )}

        <h2
          id="incoming-call-title"
          className="text-center text-xl font-bold text-slate-900 sm:text-2xl"
        >
          {title}
        </h2>

        <div className="mt-2 flex items-center gap-1.5 text-sm text-slate-500">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-green-500" />
          <span>Çalıyor...</span>
        </div>

        <div className="mt-8 grid w-full grid-cols-2 gap-4 px-2">
          <button
            ref={acceptRef}
            onClick={onAccept}
            disabled={accepting || rejecting}
            data-testid="accept-call-button"
            className="flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-2xl bg-green-600 p-4 font-semibold text-white shadow-lg transition-colors hover:bg-green-700 disabled:opacity-50 active:scale-95 sm:min-h-[80px]"
          >
            {isVideo ? <Video className="h-7 w-7" /> : <Phone className="h-7 w-7" />}
            <span className="text-sm">{accepting ? 'Kabul ediliyor...' : 'Kabul Et'}</span>
          </button>

          <button
            onClick={onReject}
            disabled={accepting || rejecting}
            data-testid="reject-call-button"
            className="flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-2xl bg-red-600 p-4 font-semibold text-white shadow-lg transition-colors hover:bg-red-700 disabled:opacity-50 active:scale-95 sm:min-h-[80px]"
          >
            <PhoneOff className="h-7 w-7" />
            <span className="text-sm">{rejecting ? 'Reddediliyor...' : 'Reddet'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
